// Feature flags (Platform Operations Console P3). Global on/off + gradual rollout
// (% of tenants, deterministic) + explicit per-tenant overrides, with a resolution
// preview. auth-service /platform/feature-flags. Flag changes are audited server-side.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Switch, Button, Space, Typography, Modal, Form, Input, InputNumber,
  Drawer, Popconfirm, message, Empty, Alert,
} from 'antd';
import { ReloadOutlined, PlusOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/console';
import * as api from '../../services/featureFlagService';

const { Text } = Typography;

export const FeatureFlagsPage: React.FC = () => {
  const [flags, setFlags] = useState<api.FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState<api.FeatureFlag | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const [overridesFor, setOverridesFor] = useState<api.FeatureFlag | null>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      setFlags(await api.listFlags());
    } catch {
      if (initial) message.error('Could not load feature flags');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { void load(true); }, [load]);

  const openCreate = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ enabled: false, rolloutPercentage: 0 }); setCreateOpen(true); };
  const openEdit = (f: api.FeatureFlag) => { setEditing(f); form.setFieldsValue({ ...f }); setCreateOpen(true); };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      if (editing) await api.updateFlag(editing.id, { description: v.description, enabled: v.enabled, rolloutPercentage: v.rolloutPercentage });
      else await api.createFlag(v);
      message.success('Saved');
      setCreateOpen(false);
      await load(false);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Save failed');
    }
  };

  const quickToggle = async (f: api.FeatureFlag, enabled: boolean) => {
    try { await api.updateFlag(f.id, { enabled }); await load(false); } catch { message.error('Could not update flag'); }
  };
  const remove = async (f: api.FeatureFlag) => {
    try { await api.deleteFlag(f.id); message.success('Deleted'); await load(false); } catch { message.error('Delete failed'); }
  };

  const columns: ColumnsType<api.FeatureFlag> = [
    { title: 'Key', dataIndex: 'key', render: (k: string) => <Text code>{k}</Text> },
    { title: 'Description', dataIndex: 'description', render: (d: string) => d || <Text type="secondary">—</Text> },
    {
      title: 'Global', dataIndex: 'enabled', width: 90,
      render: (e: boolean, f) => <Switch size="small" checked={e} onChange={(v) => quickToggle(f, v)} />,
    },
    {
      title: 'Rollout', dataIndex: 'rolloutPercentage', width: 110,
      render: (p: number, f) => (p > 0 && !f.enabled ? <Tag color="blue">{p}%</Tag> : <Text type="secondary">{p ? `${p}%` : '—'}</Text>),
    },
    {
      title: 'Overrides', dataIndex: 'overrideCount', width: 110,
      render: (n: number, f) => (
        <Button size="small" type="link" onClick={() => setOverridesFor(f)}>{n || 0} override{n === 1 ? '' : 's'}</Button>
      ),
    },
    {
      title: '', width: 140,
      render: (_: unknown, f) => (
        <Space>
          <Button size="small" type="link" onClick={() => openEdit(f)}>Edit</Button>
          <Popconfirm title={`Delete "${f.key}"?`} okButtonProps={{ danger: true }} onConfirm={() => remove(f)}>
            <Button size="small" type="link" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Feature flags"
        subtitle="Global switches, gradual rollout and per-tenant overrides"
        actions={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New flag</Button>
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>Refresh</Button>
          </Space>
        }
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Kill-switch keys"
        description={
          <>
            Enabling <Text code>platform.maintenance</Text> puts the whole platform into maintenance
            (503 for tenant traffic; operators keep access). <Text code>maintenance.&lt;service&gt;</Text>{' '}
            (e.g. <Text code>maintenance.payroll</Text>) takes down a single upstream. The gateway
            enforces these within ~30s.
          </>
        }
      />

      <Table rowKey="id" size="small" loading={loading} dataSource={flags} columns={columns} pagination={false} />

      <Modal
        title={editing ? `Edit ${editing.key}` : 'New feature flag'}
        open={createOpen}
        onOk={submit}
        onCancel={() => setCreateOpen(false)}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="key" label="Key" rules={[{ required: true, pattern: /^[a-z][a-z0-9_.-]{1,99}$/, message: 'lowercase, alphanumeric with _ . -' }]}>
            <Input placeholder="new_dashboard" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What this flag controls" />
          </Form.Item>
          <Form.Item name="enabled" label="Enabled globally" valuePropName="checked" extra="On = every tenant. Off = fall through to rollout % / overrides.">
            <Switch />
          </Form.Item>
          <Form.Item name="rolloutPercentage" label="Rollout %" extra="Applies only when not globally enabled: deterministic share of tenants.">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {overridesFor && (
        <OverridesDrawer flag={overridesFor} onClose={() => { setOverridesFor(null); void load(false); }} />
      )}
    </div>
  );
};

// Per-tenant overrides + resolution preview for one flag.
const OverridesDrawer: React.FC<{ flag: api.FeatureFlag; onClose: () => void }> = ({ flag, onClose }) => {
  const [rows, setRows] = useState<api.FlagOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState('');
  const [resolution, setResolution] = useState<api.FlagResolution | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.listOverrides(flag.id)); } catch { message.error('Could not load overrides'); } finally { setLoading(false); }
  }, [flag.id]);
  useEffect(() => { void load(); }, [load]);

  const add = async (enabled: boolean) => {
    if (!tenantId.trim()) { message.warning('Enter a tenant id'); return; }
    try { await api.setOverride(flag.id, tenantId.trim(), enabled); message.success('Override set'); await load(); }
    catch (e: any) { message.error(e?.response?.data?.message || 'Failed'); }
  };
  const clear = async (tid: string) => {
    try { await api.clearOverride(flag.id, tid); await load(); } catch { message.error('Failed'); }
  };
  const preview = async () => {
    if (!tenantId.trim()) { message.warning('Enter a tenant id'); return; }
    try { setResolution(await api.evaluateFlag(flag.id, tenantId.trim())); } catch { message.error('Could not evaluate'); }
  };

  return (
    <Drawer title={<Space><Text code>{flag.key}</Text> overrides</Space>} width={620} open onClose={onClose}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div>
          <Text strong>Tenant</Text>
          <Space.Compact style={{ display: 'flex', marginTop: 6 }}>
            <Input placeholder="tenant UUID" value={tenantId} onChange={(e) => setTenantId(e.target.value)} />
            <Button icon={<ExperimentOutlined />} onClick={preview}>Preview</Button>
            <Button onClick={() => add(true)} type="primary">Force on</Button>
            <Button onClick={() => add(false)} danger>Force off</Button>
          </Space.Compact>
          {resolution && (
            <div style={{ marginTop: 8 }}>
              Resolves to <Tag color={resolution.enabled ? 'success' : 'default'}>{resolution.enabled ? 'ON' : 'OFF'}</Tag>
              <Text type="secondary">({resolution.reason})</Text>
            </div>
          )}
        </div>

        {rows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No per-tenant overrides" />
        ) : (
          <Table
            rowKey="id" size="small" loading={loading} pagination={false} dataSource={rows}
            columns={[
              { title: 'Tenant', dataIndex: 'tenantId', render: (t: string) => <Text code>{t}</Text> },
              { title: 'Value', dataIndex: 'enabled', width: 90, render: (e: boolean) => <Tag color={e ? 'success' : 'default'}>{e ? 'ON' : 'OFF'}</Tag> },
              { title: '', width: 80, render: (_: unknown, r) => <Button size="small" type="link" danger onClick={() => clear(r.tenantId)}>Clear</Button> },
            ]}
          />
        )}
      </Space>
    </Drawer>
  );
};

export default FeatureFlagsPage;
